/**
 * responseStore — cache-first response resolution for the frog's voice.
 *
 * Policy (agreed with Natalya, June 2026): responses are pre-written and
 * cached wherever possible; live AI generation is the FALLBACK, used only when
 * a line isn't in the cache. This module answers one question for the player:
 *
 *     "Do we already have this line, or do we need the AI to say it?"
 *
 * It is intentionally tiny and pure (no React, no network) so it can be unit
 * tested and reused. The manifest is generated from disk by
 * scripts/build-response-manifest.mjs — re-run that after pre-generating audio.
 */
import manifest from './responseManifest.js';

const CACHED = new Set(manifest.cached);

/**
 * Map a logical audio path used in the app (e.g. "/audio/cow-multi-correct.mp3")
 * to its cache key (basename without extension), matching resolveAudioPath in
 * DemoApp.jsx.
 */
export function cacheKey(audioPath) {
  if (!audioPath) return '';
  return audioPath
    .replace(/^\/audio(?:-gemini\/frog)?\//, '')
    .replace(/\.(mp3|wav)$/i, '');
}

/**
 * Decide how a given line should be voiced.
 * @param {string} audioPath  Logical path, e.g. "/audio/greeting.mp3".
 * @returns {{ cached: boolean, key: string, source: 'cache' | 'ai' }}
 */
export function resolveResponse(audioPath) {
  const key = cacheKey(audioPath);
  const cached = key !== '' && CACHED.has(key);
  return { cached, key, source: cached ? 'cache' : 'ai' };
}

/** True if we have a pre-generated line for this path. */
export function isCached(audioPath) {
  return resolveResponse(audioPath).cached;
}

export const manifestMeta = {
  count: CACHED.size,
  generatedAt: manifest.generatedAt,
  voiceDir: manifest.voiceDir,
};
